import { createFileRoute } from "@tanstack/react-router";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ReportCard } from "@/features/reports/ReportCard";
import { REPORTS } from "@/features/reports/registry";
import type { ReportCategory } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/")({
  component: ReportsIndex,
});

const FALLBACK_CATEGORIES: ReportCategory[] = ["Asset", "Maintenance", "Compliance", "Finance", "Insurance", "Audit", "Ticketing"];

function ReportsIndex() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState<string>("All");
  const CATEGORIES: string[] = FALLBACK_CATEGORIES as string[];

  const filtered = useMemo(() => REPORTS.filter((r) => {
    if (cat !== "All" && r.category !== cat) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      if (!r.title.toLowerCase().includes(s) && !r.id.toLowerCase().includes(s) && !r.description.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [search, cat]);

  const availableCount = REPORTS.filter((r) => r.status === "built" || r.status === "partial").length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports Centre</h1> <ReadOnlyPill module="reports" />
          <p className="text-sm text-muted-foreground">{availableCount} of {REPORTS.length} reports available</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reports…" className="w-64" />
        <button onClick={() => setCat("All")} className={`rounded-full border px-3 py-1 text-xs ${cat === "All" ? "border-primary bg-primary text-primary-foreground" : "border-input"}`}>All</button>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`rounded-full border px-3 py-1 text-xs ${cat === c ? "border-primary bg-primary text-primary-foreground" : "border-input"}`}>{c}</button>
        ))}
      </div>

      {CATEGORIES.map((c) => {
        const items = filtered.filter((r) => r.category === c);
        if (items.length === 0) return null;
        return (
          <section key={c} className="space-y-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              {c} <Badge variant="secondary">{items.length}</Badge>
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((r) => <ReportCard key={r.id} report={r} />)}
            </div>
          </section>
        );
      })}

      {filtered.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No reports match your search.</p>}
    </div>
  );
}
